import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, FolderOpen, BarChart } from "lucide-react";

export default function Categories() {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", description: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      toast.error("Erro ao carregar categorias");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/categories`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Categoria criada!");
      setDialogOpen(false);
      setFormData({ name: "", description: "" });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar categoria");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Tem certeza que deseja excluir "${name}"?`)) return;

    try {
      await axios.delete(`${API}/categories/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Categoria excluída");
      fetchCategories();
    } catch (error) {
      toast.error("Erro ao excluir categoria");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const defaultCategories = [
    { name: "Gôndola", description: "Prateleiras de gôndola padrão" },
    { name: "Refrigerador", description: "Refrigeradores e geladeiras" },
    { name: "Prateleira", description: "Prateleiras avulsas" },
    { name: "Freezer", description: "Freezers horizontais e verticais" }
  ];

  const createDefaultCategory = async (cat) => {
    try {
      await axios.post(`${API}/categories`, cat, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Categoria "${cat.name}" criada!`);
      fetchCategories();
    } catch (error) {
      toast.error(`Erro ao criar "${cat.name}"`);
    }
  };

  return (
    <Sidebar>
      <div className="p-6 lg:p-8" data-testid="categories-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-chivo font-bold text-3xl tracking-tight mb-1">Categorias</h1>
            <p className="text-zinc-500 text-sm">
              Organize suas análises por tipo de equipamento
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="add-category-btn"
                className="h-10 px-5 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white gap-2"
              >
                <Plus size={18} strokeWidth={1.5} />
                Nova Categoria
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md rounded-none">
              <DialogHeader>
                <DialogTitle className="font-chivo font-bold text-xl">Nova Categoria</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium mb-2 block">
                    Nome *
                  </Label>
                  <Input
                    id="name"
                    data-testid="category-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Gôndola Central"
                    className="h-11 rounded-none"
                  />
                </div>
                <div>
                  <Label htmlFor="description" className="text-sm font-medium mb-2 block">
                    Descrição
                  </Label>
                  <Textarea
                    id="description"
                    data-testid="category-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descrição opcional..."
                    className="rounded-none resize-none"
                    rows={3}
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    className="flex-1 h-10 rounded-none"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    data-testid="save-category-btn"
                    disabled={submitting}
                    className="flex-1 h-10 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white"
                  >
                    {submitting ? "Salvando..." : "Salvar"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent animate-spin rounded-full" />
          </div>
        ) : categories.length === 0 ? (
          <div className="bg-white border border-zinc-200 p-8">
            <div className="text-center mb-8">
              <FolderOpen className="w-12 h-12 mx-auto mb-4 text-zinc-300" />
              <p className="text-zinc-500 mb-2">Nenhuma categoria criada ainda</p>
              <p className="text-sm text-zinc-400">Crie categorias para organizar suas análises</p>
            </div>

            <div className="border-t border-zinc-200 pt-6">
              <p className="text-sm font-medium mb-4">Sugestões de categorias:</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {defaultCategories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => createDefaultCategory(cat)}
                    className="p-4 border border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50 text-left transition-colors"
                  >
                    <p className="font-medium text-sm">{cat.name}</p>
                    <p className="text-xs text-zinc-500 mt-1">{cat.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => (
              <div 
                key={category.id}
                className="bg-white border border-zinc-200 p-5 hover:border-zinc-300 transition-colors"
                data-testid={`category-item-${category.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 bg-zinc-100 flex items-center justify-center">
                    <BarChart className="w-5 h-5 text-zinc-500" strokeWidth={1.5} />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(category.id, category.name)}
                    className="h-8 w-8 hover:bg-red-50 hover:text-red-600"
                    data-testid={`delete-category-${category.id}`}
                  >
                    <Trash2 size={16} strokeWidth={1.5} />
                  </Button>
                </div>
                <h3 className="font-chivo font-bold text-lg mb-1">{category.name}</h3>
                {category.description && (
                  <p className="text-sm text-zinc-500 mb-3 line-clamp-2">{category.description}</p>
                )}
                <p className="text-xs text-zinc-400">
                  Criado em {formatDate(category.created_at)}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </Sidebar>
  );
}
